/**
 * Manager for audio recording lifecycle with silence detection.
 * Uses expo-av for recording and auto-stops when silence is detected.
 */

import { Audio } from 'expo-av';
import { File as ExpoFile } from 'expo-file-system';

/** Configurable silence detection constants */
const SILENCE_THRESHOLD_DB = -45;
/** Auto-stop once the accumulated silence balance reaches this many ms. */
const SILENCE_DURATION_MS = 1200;
const METERING_POLL_INTERVAL_MS = 250;
/** Loud readings drain the silence accumulator at this multiplier of the
 *  poll interval. Setting it >1 means a brief noise spike costs more than
 *  it earns back, so the timer recovers quickly from chirps but is wiped
 *  by sustained speech. */
const LOUD_READING_DRAIN_MULTIPLIER = 2;
const MAX_RECORDING_DURATION_MS = 3 * 60 * 1000; // 3 minutes

type RecordingStartCallback = () => void;
type RecordingStopCallback = (fileUri: string | null) => void;

export class AudioRecordingManager {
  private static instance: AudioRecordingManager;

  private recording: Audio.Recording | null = null;
  private meteringInterval: ReturnType<typeof setInterval> | null = null;
  private maxDurationTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Running balance of "silence credit" in ms. Quiet poll readings add to it,
   *  loud readings subtract. Stop when it reaches SILENCE_DURATION_MS. This
   *  hysteresis prevents single-poll background noise spikes from infinitely
   *  resetting the silence countdown. */
  private silenceMs: number = 0;
  private onRecordingStart: RecordingStartCallback | null = null;
  private onRecordingStop: RecordingStopCallback | null = null;

  static getInstance(): AudioRecordingManager {
    if (!AudioRecordingManager.instance) {
      AudioRecordingManager.instance = new AudioRecordingManager();
    }
    return AudioRecordingManager.instance;
  }

  async startRecording(callbacks?: {
    onStart?: RecordingStartCallback;
    onStop?: RecordingStopCallback;
  }): Promise<void> {
    // Clean up any previous recording
    await this.cleanup();

    this.onRecordingStart = callbacks?.onStart ?? null;
    this.onRecordingStop = callbacks?.onStop ?? null;

    // Configure audio mode for recording
    await Audio.setAudioModeAsync({
      allowsRecordingIOS: true,
      playsInSilentModeIOS: true,
    });

    // Create and start recording with metering enabled
    const recordingOptions: Audio.RecordingOptions = {
      ...Audio.RecordingOptionsPresets.HIGH_QUALITY,
      android: {
        ...Audio.RecordingOptionsPresets.HIGH_QUALITY.android,
        extension: '.m4a',
        outputFormat: Audio.AndroidOutputFormat.MPEG_4,
        audioEncoder: Audio.AndroidAudioEncoder.AAC,
      },
      ios: {
        ...Audio.RecordingOptionsPresets.HIGH_QUALITY.ios,
        extension: '.m4a',
        outputFormat: Audio.IOSOutputFormat.MPEG4AAC,
      },
      isMeteringEnabled: true,
    };

    const { recording } = await Audio.Recording.createAsync(recordingOptions);
    this.recording = recording;
    this.silenceMs = 0;

    this.onRecordingStart?.();
    this.startSilenceDetection();
    this.startMaxDurationTimer();
  }

  async stopRecording(): Promise<string | null> {
    this.clearTimers();

    if (!this.recording) return null;

    try {
      await this.recording.stopAndUnloadAsync();
      const uri = this.recording.getURI();
      this.recording = null;

      // Reset audio mode
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
      });

      this.onRecordingStop?.(uri ?? null);
      return uri;
    } catch (error) {
      console.error('[AudioRecordingManager] Error stopping recording:', error);
      this.recording = null;
      return null;
    }
  }

  async cleanup(): Promise<void> {
    this.clearTimers();

    if (this.recording) {
      try {
        const status = await this.recording.getStatusAsync();
        if (status.isRecording) {
          await this.recording.stopAndUnloadAsync();
        }
        const uri = this.recording.getURI();
        this.recording = null;

        // Delete temp file
        if (uri) {
          try { new ExpoFile(uri).delete(); } catch { /* ignore */ }
        }
      } catch {
        this.recording = null;
      }
    }

    this.onRecordingStart = null;
    this.onRecordingStop = null;
  }

  get isRecording(): boolean {
    return this.recording !== null;
  }

  private startSilenceDetection(): void {
    this.meteringInterval = setInterval(async () => {
      if (!this.recording) {
        this.clearTimers();
        return;
      }

      try {
        const status = await this.recording.getStatusAsync();
        if (!status.isRecording) return;

        const metering = status.metering ?? -160;

        if (metering < SILENCE_THRESHOLD_DB) {
          // Quiet reading — add the poll interval to the silence balance,
          // capped at the trigger threshold so the value stays bounded.
          this.silenceMs = Math.min(
            SILENCE_DURATION_MS,
            this.silenceMs + METERING_POLL_INTERVAL_MS,
          );
          if (this.silenceMs >= SILENCE_DURATION_MS) {
            await this.stopRecording();
          }
        } else {
          // Loud reading — drain the balance at a higher rate so a single
          // background-noise spike doesn't undo a long quiet stretch, but
          // sustained speech still wipes it.
          this.silenceMs = Math.max(
            0,
            this.silenceMs - METERING_POLL_INTERVAL_MS * LOUD_READING_DRAIN_MULTIPLIER,
          );
        }
      } catch {
        // Recording may have been stopped externally
        this.clearTimers();
      }
    }, METERING_POLL_INTERVAL_MS);
  }

  private startMaxDurationTimer(): void {
    this.maxDurationTimeout = setTimeout(async () => {
      if (this.recording) {
        await this.stopRecording();
      }
    }, MAX_RECORDING_DURATION_MS);
  }

  private clearTimers(): void {
    if (this.meteringInterval) {
      clearInterval(this.meteringInterval);
      this.meteringInterval = null;
    }
    if (this.maxDurationTimeout) {
      clearTimeout(this.maxDurationTimeout);
      this.maxDurationTimeout = null;
    }
    this.silenceMs = 0;
  }
}

export const audioRecordingManager = AudioRecordingManager.getInstance();
